// /utils/errorHandling/getProfessionalErrorMessage.tsx
import React from 'react';

// Extracts a displayable string from anything thrown/rejected.
// Handles: string (from rejectWithValue), Error (from apiClient), arbitrary objects.
export const extractErrorMessage = (err: unknown, fallback?: string): string => {
  if (typeof err === 'string') return err;
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object') {
    const e = err as any;
    if (typeof e.detail === 'string') return e.detail;
    if (typeof e.message === 'string') return e.message;
    if (Array.isArray(e.error)) return e.error.join(' ');
    if (typeof e.error === 'string') return e.error;
  }
  return fallback ?? 'An unexpected error occurred. Please try again.';
};

// Surface the raw API error string verbatim so engineers can debug from the UI.
// Only HTML/server-stack-trace responses get a generic fallback, since those
// aren't safe or readable to display as-is.
export const getProfessionalErrorMessage = (error: string): string => {
  const raw = (error ?? '').toString().trim();

  if (!raw) return 'An unexpected error occurred. Please try again.';

  const isHtml =
    /<!doctype html/i.test(raw) ||
    /<html[\s>]/i.test(raw) ||
    /<head[\s>]/i.test(raw) ||
    /<body[\s>]/i.test(raw) ||
    /<title>.*?<\/title>/i.test(raw) ||
    /<h1>.*?<\/h1>/i.test(raw);

  if (isHtml) {
    const stripped = raw
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const title = stripped.match(/^([^.!?]+[.!?])/)?.[1] ?? stripped.slice(0, 200);
    return `Server error: ${title}`;
  }

  // Python/Django traceback or other server-stack noise — collapse to a generic line.
  const stackTracePatterns = [
    /Traceback \(most recent call last\)/i,
    /File ".*\.py"/i,
    /django\./i,
    /at .*\.\w+\s+\(.*:\d+:\d+\)/, // JS stack frame
  ];
  if (stackTracePatterns.some((p) => p.test(raw))) {
    return 'Service is currently unavailable due to a system issue. Please try again in a few minutes. If the problem persists, contact support.';
  }

  return raw;
};

export const renderAlertMessage = (error: string): React.ReactNode => {
  const message = getProfessionalErrorMessage(error);

  if (message.includes('unavailable')) {
    return (
      <div>
        <div>{message}</div>
      </div>
    );
  }

  return message;
};
